import { useState } from 'react';
import { Button, Popover, Text } from '@customerio/pluma-components/react';
import { useAuth } from '@/services/auth';
import { isFlexPlan } from '@/services/auth/plan-slugs';
import { useOpenActivityHistoryAddon } from '../hooks/use-open-activity-history-addon';
import { catalogPriceCents } from '../hooks/use-addon-price';
import { useAddons } from '../hooks/use-addons';
import { activityHistory, type ActivityHistoryKind, gatedIncludedDays } from '../utils/activity-history';
import { formatAddonPriceCents, formatShortDateWithYear, perBillingCycle } from '../utils/addon-purchase';
import { FlexMark } from '@/components/flex-mark';
import { AddonAction, AddonIncludedBox, AddonNote, AddonOfferBox, AddonPopover } from './addon-popover';

type ActivityHistoryChipProps = {
  kind: ActivityHistoryKind;
};

/** How far back a Flex account can see message or profile activity, and the add-on that extends it. */
export function ActivityHistoryChip({ kind }: ActivityHistoryChipProps) {
  const { account } = useAuth();
  const { data: addons } = useAddons();
  // Until they load, or when they fail to, a retention holder would see the upsell.
  if (!isFlexPlan(account) || addons == null) return null;
  if (addons.purchased.some((addon) => addon.product === 'data_retention')) return null;

  const priceCents = catalogPriceCents(addons, 'data_retention');
  return <FlexActivityHistoryChip kind={kind} price={priceCents == null ? null : formatAddonPriceCents(priceCents)} />;
}

type FlexActivityHistoryChipProps = ActivityHistoryChipProps & { price: string | null };

function FlexActivityHistoryChip({ kind, price }: FlexActivityHistoryChipProps) {
  const priceLine = perBillingCycle(price);
  const [isOpen, setIsOpen] = useState(false);
  const { account } = useAuth();
  const history = activityHistory(kind, new Date(), gatedIncludedDays(account, kind));
  const openAddon = useOpenActivityHistoryAddon();

  return (
    <Popover isOpen={isOpen} onOpenChange={setIsOpen} withArrow={false} placement="bottom-end">
      {(context) => (
        <Button
          ref={context.reference.setReference}
          {...context.reference.getReferenceProps()}
          variant="secondary"
          isActive={isOpen}
          elementBefore={<FlexMark />}
          data-test-activity-history-chip={kind}
        >
          <Text text="product-p" color="inherit">
            Data retention
          </Text>
        </Button>
      )}

      <AddonPopover title={history.heading}>
        <AddonIncludedBox label="Included" figure={`Back to ${formatShortDateWithYear(history.lookbackStart)}`} />
        <AddonOfferBox
          label="With data retention"
          price={priceLine}
          value={`Back to ${formatShortDateWithYear(history.addonLookbackStart)}`}
        />
        <AddonNote>
          Nothing is deleted. Add it and older activity reappears. It also extends metrics, journeys, condition
          lookback, and the audit log.
        </AddonNote>
        <AddonAction
          label="Add data retention"
          onClick={() => {
            setIsOpen(false);
            void openAddon();
          }}
        />
      </AddonPopover>
    </Popover>
  );
}
